'use client';
import { ReactNode } from 'react';
import { SvgSunGlow } from '@icons/designSystemIcons/SvgSunGlow';
import { useIsMobileDevice } from 'app/hooks/useIsMobileDevice';
import { Product } from 'app/types/product';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text } from 'designSystem/Texts/Texts';

import AnimateOnViewport from './AnimateOnViewport';
import ResponsiveImage from './ResponsiveImage';

type ProductBenefitsSectionProps = {
  product: Product;
  children?: ReactNode;
  benefitDetails?: any[];
  benefitsMobileDifferent?: boolean;
  slug?: string;
  /** Sustituye el título de un grupo por su `order` ({ 0: 'físicos' }). */
  benefitsTitles?: Record<number, string>;
  /** Lo calcula el Server Component padre con `isMobileSSR()` (landings). */
  isMobile?: boolean;
};

const MOBILE_PNG_SLUGS = ['perdida-de-peso-glp-1'];

export default function ProductBenefitsSection({
  product,
  children,
  benefitDetails,
  benefitsMobileDifferent = false,
  slug = '',
  benefitsTitles: customBenefitsTitles,
  isMobile,
}: ProductBenefitsSectionProps) {
  const detectedIsMobile = useIsMobileDevice();
  const isMobileDevice = isMobile ?? detectedIsMobile;

  let benefits = product.extraInformation?.benefitsInformation?.benefitDetails;
  if (benefitDetails) {
    benefits = benefitDetails;
  }
  const typesOfBenefits = benefits
    .map(benefit => benefit.order)
    .filter((order, index, self) => self.indexOf(order) === index);
  // Con títulos propios, los grupos salen en orden (0 antes que 2).
  if (customBenefitsTitles) typesOfBenefits.sort((a, b) => a - b);

  const benefitsTitles: Record<number, string> = {
    ...['Estéticos', 'Psicológicos', 'Funcionales'],
    ...customBenefitsTitles,
  };

  if (!typesOfBenefits || typesOfBenefits.length === 0) {
    return null;
  }

  // Antes eran dos fondos CSS (JPG de 580 y 300 KB) y, como el layout se
  // decidía en cliente, el navegador descargaba los dos. Ahora es una sola
  // imagen optimizada por next/image, elegida por media query.
  // v=5: se sirven los originales de diseño sin optimizar (2-nov); la caché de imágenes dura 30 días.
  const desktopSrc = `/images/pdp/${slug ? slug : product.extraInformation!.slug}-benefits.jpg?v=6`;
  // La de móvil de GLP-1 llegó en PNG y se sirve tal cual, sin pasarla a JPG.
  const mobileExt =
    benefitsMobileDifferent &&
    MOBILE_PNG_SLUGS.includes(product.extraInformation!.slug)
      ? 'png'
      : 'jpg';
  const mobileSrc = `/images/pdp/${product.extraInformation!.slug}-benefits${benefitsMobileDifferent ? '-mobile' : ''}.${mobileExt}?v=6`;

  const background = (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <ResponsiveImage
        mobileSrc={mobileSrc}
        desktopSrc={desktopSrc}
        alt=""
        sizes="100vw"
        unoptimized
        className="object-cover object-center md:object-top"
      />
    </div>
  );

  if (!isMobileDevice) {
    return (
      <div className="w-full">
        <Container className="md:px-0 relative " style={{ maxWidth: '100%' }}>
          {background}
          <div className="relative z-10 p-6 ml-0 md:ml-40 pt-8">
            <Text
              isAnimated
              className="text-4xl mt-4 mb-4 text-white font-gtUltra font-bold"
            >
              ¿Qué conseguirás?
            </Text>
            <div className="w-full flex flex-col gap-2">
              {typesOfBenefits.map(x => (
                <div key={x} className="w-full overflow-hidden">
                  <AnimateOnViewport>
                    <div className="w-full p-3">
                      <Flex layout="col-left" className="w-full">
                        <Text
                          size="lg"
                          className="font-semibold mb-2 text-white"
                        >
                          Beneficios {benefitsTitles[x]}
                        </Text>
                        <div className="w-full">
                          {benefits
                            .filter(benefit => benefit.order === x)
                            .sort((a, b) =>
                              a.creationDate > b.creationDate ? 0 : -1
                            )
                            .map(benefit => (
                              <li key={benefit.id} className="flex mb-1">
                                <SvgSunGlow className="text-white h-4 w-4 md:h-4 md:w-4 shrink-0 mr-1" />
                                <p className="ml-2 text-white">
                                  {benefit.title}
                                </p>
                              </li>
                            ))}
                        </div>
                      </Flex>
                    </div>
                  </AnimateOnViewport>
                </div>
              ))}
            </div>
          </div>
          {children && (
            <Flex
              layout="col-center"
              className="w-full px-4 py-8 md:px-0 md:py-12 md:pl-0 flex justify-center items-center mb-4"
            >
              {children}
            </Flex>
          )}
        </Container>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Container className="md:px-0 relative">
        {background}
        <div className="relative z-10 p-6">
          <Text
            isAnimated
            className="mb-4 text-white font-gtUltra text-xldr font-bold"
          >
            ¿Qué conseguirás?
          </Text>
          <div className="w-full flex flex-col gap-4">
            {typesOfBenefits.map(x => (
              <div key={x} className="w-full overflow-hidden">
                <AnimateOnViewport>
                  <div className="w-full p-3">
                    <Flex layout="col-left" className="w-full">
                      <Text size="lg" className="font-semibold mb-4 text-white">
                        Beneficios {benefitsTitles[x]}
                      </Text>
                      <div className="w-full">
                        {benefits
                          .filter(benefit => benefit.order === x)
                          .sort((a, b) =>
                            a.creationDate > b.creationDate ? 0 : -1
                          )
                          .map(benefit => (
                            <li key={benefit.id} className="flex mb-2">
                              <SvgSunGlow className="text-white h-6 w-6 md:h-8 md:w-8 shrink-0 mr-2" />
                              <p className="ml-2 text-white">{benefit.title}</p>
                            </li>
                          ))}
                      </div>
                    </Flex>
                  </div>
                </AnimateOnViewport>
              </div>
            ))}
          </div>
        </div>
        {children && (
          <Flex
            layout="col-center"
            className="w-full px-4 py-8 md:px-0 md:py-12 md:pl-0 flex justify-center items-center"
          >
            {children}
          </Flex>
        )}
      </Container>
    </div>
  );
}
